import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE_URL, getAuthHeaders, handleUnauthorized } from "../../utils/api";
import {
  buildChallanData,
  challanStyles,
  printChallanSheets,
  renderChallanSheetHtml,
} from "../shared/fees/challanDocument";
import "./parent.css";

const fmtPKR = (n) => `PKR ${Number(n || 0).toLocaleString("en-PK")}`;

const statusChipClass = (status) => {
  const s = String(status || "Unpaid").toLowerCase();
  if (s === "paid") return "pp-chip pp-chip--ok";
  if (s.includes("pending")) return "pp-chip pp-chip--pending";
  return "pp-chip pp-chip--warn";
};

function ParentFees({ childrenList, loading: parentLoading, onLogout }) {
  const [selectedChildId, setSelectedChildId] = useState("");
  const [challans, setChallans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [toast, setToast] = useState("");
  const fileInputRef = useRef(null);
  const uploadTargetRef = useRef(null);

  const loadChallans = async (childId) => {
    if (!childId) {
      setChallans([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/parents/me/children/${childId}/challans`,
        { headers: getAuthHeaders() },
      );
      if (res.status === 401) {
        handleUnauthorized(onLogout);
        return;
      }
      if (!res.ok) throw new Error("Failed to load challans");
      const data = await res.json();
      setChallans(Array.isArray(data) ? data : []);
    } catch (err) {
      setChallans([]);
      setError(err.message || "Could not load fee challans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!childrenList.length) return;
    const active = sessionStorage.getItem("als_parent_active_child_id");
    if (active && childrenList.some((c) => String(c.id) === String(active))) {
      setSelectedChildId(String(active));
    } else if (!selectedChildId) {
      setSelectedChildId(String(childrenList[0].id));
    }
  }, [childrenList, selectedChildId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!selectedChildId) {
        setChallans([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/parents/me/children/${selectedChildId}/challans`,
          { headers: getAuthHeaders() },
        );
        if (res.status === 401) {
          handleUnauthorized(onLogout);
          return;
        }
        if (!res.ok) throw new Error("Failed to load challans");
        const data = await res.json();
        if (alive) setChallans(Array.isArray(data) ? data : []);
      } catch (err) {
        if (alive) {
          setChallans([]);
          setError(err.message || "Could not load fee challans");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [selectedChildId, onLogout]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(""), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  const selectedChild = useMemo(
    () => childrenList.find((c) => String(c.id) === String(selectedChildId)),
    [childrenList, selectedChildId],
  );

  const buildSheet = (challan) => {
    const student = {
      id: selectedChild?.id,
      student_id: selectedChild?.student_id || challan.studentRollNo,
      name: selectedChild?.name || challan.studentName,
      campus_name: selectedChild?.campus_name || challan.campusName,
      class_name: selectedChild?.class_name || challan.studentClass,
      class_section: selectedChild?.class_section || challan.studentSection,
    };
    return buildChallanData({
      student,
      feeStructure: {
        components: challan.components,
        concessionAmount: challan.concessionAmount,
        month: challan.month,
        dueDate: challan.dueDate,
      },
      challan,
      previousDues: 0,
    });
  };

  const openPreview = (challan) => {
    setPreview(buildSheet(challan));
  };

  const printOne = (challan) => {
    const sheet = buildSheet(challan);
    printChallanSheets([sheet], `Fee Challan — ${sheet.challanNumber}`);
  };

  const startUpload = (challan) => {
    uploadTargetRef.current = challan;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const challan = uploadTargetRef.current;
    if (!file || !challan) return;

    setUploadingId(challan.id);
    setError("");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(
        `${API_BASE_URL}/api/parents/me/challans/${challan.id}/paid-proof`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: form,
        },
      );
      if (res.status === 401) {
        handleUnauthorized(onLogout);
        return;
      }
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(payload.detail || "Upload failed");
      }
      setToast("Paid challan uploaded — awaiting verification");
      await loadChallans(selectedChildId);
    } catch (err) {
      setError(err.message || "Could not upload paid challan");
    } finally {
      setUploadingId(null);
      uploadTargetRef.current = null;
    }
  };

  if (parentLoading) {
    return (
      <div className="pp-loading">
        <div className="pp-spinner" />
        <p>Loading fees…</p>
      </div>
    );
  }

  return (
    <div className="pp-page pp-animate-in">
      <header className="pp-page-header">
        <h1>Fee Challans</h1>
      </header>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,application/pdf,image/*"
        style={{ display: "none" }}
        onChange={onFileChosen}
      />

      <div className="pp-fee-toolbar">
        <label className="pp-field">
          <span>Child</span>
          <select
            value={selectedChildId}
            onChange={(e) => {
              setSelectedChildId(e.target.value);
              sessionStorage.setItem("als_parent_active_child_id", e.target.value);
            }}
          >
            {childrenList.map((child) => (
              <option key={child.id} value={child.id}>
                {child.name} ({child.student_id})
              </option>
            ))}
          </select>
        </label>
      </div>

      {toast && <div className="pp-alert pp-alert--success">{toast}</div>}
      {error && <div className="pp-alert pp-alert--error">{error}</div>}

      {loading ? (
        <div className="pp-loading">
          <div className="pp-spinner" />
          <p>Loading challans…</p>
        </div>
      ) : challans.length === 0 ? (
        <div className="pp-empty">No generated challans found for this child yet.</div>
      ) : (
        <div className="pp-panel">
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead>
                <tr>
                  <th>Challan</th>
                  <th>Category</th>
                  <th>Month</th>
                  <th>Due</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {challans.map((ch) => {
                  const status = ch.status || "Unpaid";
                  const isPaid = String(status).toLowerCase() === "paid";
                  const isPending = String(status).toLowerCase().includes("pending");
                  const canUpload = !isPaid;
                  return (
                    <tr key={ch.id}>
                      <td className="pp-mono">{ch.challanNumber}</td>
                      <td>{ch.categoryLabel || "Fee"}</td>
                      <td>{ch.month || "—"}</td>
                      <td>{ch.dueDate || "—"}</td>
                      <td>{fmtPKR(ch.netPayable)}</td>
                      <td>
                        <span className={statusChipClass(status)}>{status}</span>
                        {ch.paidProofName && !isPaid && (
                          <div className="pp-fee-proof-hint">Proof: {ch.paidProofName}</div>
                        )}
                      </td>
                      <td>
                        <div className="pp-fee-actions">
                          <button type="button" className="pp-btn pp-btn--sm" onClick={() => openPreview(ch)}>
                            View
                          </button>
                          <button
                            type="button"
                            className="pp-btn pp-btn--primary pp-btn--sm"
                            onClick={() => printOne(ch)}
                          >
                            Print
                          </button>
                          {canUpload && (
                            <button
                              type="button"
                              className="pp-btn pp-btn--gold pp-btn--sm"
                              disabled={uploadingId === ch.id}
                              onClick={() => startUpload(ch)}
                              title={isPending ? "Replace uploaded paid challan" : "Upload paid challan"}
                            >
                              {uploadingId === ch.id
                                ? "Uploading…"
                                : isPending
                                  ? "Re-upload"
                                  : "Upload"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {preview && (
        <div className="pp-modal-overlay" onClick={() => setPreview(null)}>
          <div className="pp-modal pp-challan-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pp-modal-head">
              <div>
                <h2>Fee challan preview</h2>
                <p>
                  {preview.studentName} · {preview.challanNumber}
                </p>
              </div>
              <div className="pp-fee-actions">
                <button
                  type="button"
                  className="pp-btn pp-btn--primary"
                  onClick={() => printChallanSheets([preview], `Fee Challan — ${preview.challanNumber}`)}
                >
                  Print
                </button>
                <button type="button" className="pp-btn pp-btn--ghost" onClick={() => setPreview(null)}>
                  Close
                </button>
              </div>
            </div>
            <div className="pp-challan-preview">
              <style>{challanStyles}</style>
              <div dangerouslySetInnerHTML={{ __html: renderChallanSheetHtml(preview) }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentFees;
